import { memo, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SelectItem, TmdxSelect } from '@ds';
import { AdminFormRow } from '@ui';
import { ORGAN_TYPES } from '@features/components/CaseFormBlocks/config';
import { useCanCreateKidneyCase } from '@lp/core/hooks/useCanCreateKidneyCase';
import { OrganType } from '@/api/apis/case';
import { NewOcsConsoleForm } from '../types';

type OcsConsoleOrganSelectorProps = {
  isReadOnly?: boolean;
  originalOrganType?: OrganType;
};

export const OcsConsoleOrganSelector = memo(({ isReadOnly, originalOrganType }: OcsConsoleOrganSelectorProps) => {
  const { control, setValue } = useFormContext<NewOcsConsoleForm>();
  const canCreateKidneyCase = useCanCreateKidneyCase();
  const currentOrganType = useWatch({ control, name: 'organType' });

  const organTypes = useMemo(() => {
    if (canCreateKidneyCase) return ORGAN_TYPES;

    const isExistingKidneyConsoleUnchanged =
      originalOrganType === OrganType.Kidney && currentOrganType === OrganType.Kidney;

    if (isExistingKidneyConsoleUnchanged) return ORGAN_TYPES;

    return ORGAN_TYPES.filter((organ) => organ.value !== OrganType.Kidney);
  }, [canCreateKidneyCase, originalOrganType, currentOrganType]);

  return (
    <AdminFormRow title="Organ" isRequired>
      <Controller
        name="organType"
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
          <TmdxSelect
            placeholder="Choose an option"
            aria-label="Choose an option"
            selectedKeys={[value]}
            isInvalid={invalid}
            isDisabled={isReadOnly}
            errorMessage={error?.message}
            disallowEmptySelection
            onChange={(e) => {
              onChange(e);
              setValue('trialProgramId', '');
            }}
            {...field}
          >
            {organTypes.map((organ) => (
              <SelectItem key={organ.value as string}>{organ.name}</SelectItem>
            ))}
          </TmdxSelect>
        )}
      />
    </AdminFormRow>
  );
});

OcsConsoleOrganSelector.displayName = 'OcsConsoleOrganSelector';
